import * as React from 'react';
import './ServerAdd.scss';
import { observer } from 'mobx-react';
import { getAppContext, IPanelAppContext } from '~/lib/AppContext';
import ServerAddStore from './ServerAddStore';
import { CardBody, Card, CardHeader } from 'reactstrap';
import { serverRoute } from '~/lib/helper/RouteHelper';
import { getErrorsFor, showFormError } from '~/lib/helper/ErrorHelper';
import { AsyncButton } from '~/components/AsyncButton';
import AgreeTerms from '~/components/size/AgreeTerms';
import { asNumber } from '~/lib/helper/StringHelper';
import BillingDetails from '~/components/BillingDetails';
import UnpaidInvoiceBlock from '~/components/UnpaidInvoiceBlock';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import ViewInMpanel from '~/components/ViewInMpanel';

import { PrePaymentFrequencyOption } from '~/lib/ApiModel';
import ServerAddForm from './ServerAddForm';

type ServerAddProps = {
    appContext: IPanelAppContext;
    search: Map<string, string>;
};

@observer
export default class ServerAddPage extends React.Component<ServerAddProps> {
    store: ServerAddStore;

    unmountHelper = new UnmountHelper(this);

    constructor(props: ServerAddProps) {
        super(props);
        const { apiClient, siteApiClient } = props.appContext;
        this.store = new ServerAddStore(apiClient, siteApiClient);
    }

    componentDidMount() {
        // Dispose the store on unmount so its inherited ServerSizeConfigStore reactions don't outlive each visit
        // (mirrors ChangeSizePanel; see PanelSite.md "Disposing reactions").
        this.unmountHelper.onUnmount(() => this.store.dispose());
        void this.store.initialize();
        void this.store.unpaidInvoices.load();
        if (this.props.search.has('vpc')) {
            const network = asNumber(this.props.search.get('vpc'));
            if (network) this.store.selectNetwork(network);
        }
    }

    renderAgreeTerms() {
        return (
            <AgreeTerms
                checked={this.store.agreeTerms}
                errors={getErrorsFor(this.store.currentErrors, 'agreeTerms')}
                onChange={(checked) => this.store.setAgreeTerms(checked)}
            />
        );
    }

    async doAddServer() {
        try {
            const res = await this.store.addServer();
            if (res.paypal_redirect?.authorization_url) {
                window.location.href = res.paypal_redirect.authorization_url;
            } else {
                this.props.appContext.navigate(serverRoute(res.server), true);
            }
        } catch (e) {
            // Field-level validation errors render inline; showFormError surfaces only
            // form-level / non-validation errors via the modal.
            showFormError(e, { errorSummary: 'Unable to create server' });
        }
    }

    renderBillingSummary(): React.ReactElement {
        const paymentFrequencyProps = this.store.selectedPaymentFrequencyOption
            ? {
                  prePaymentFrequency: this.store.selectedPaymentFrequencyOption,
                  prePaymentFrequencyOptions: this.store.serverOptions.paymentFrequencies.items,
                  onPrePaymentFrequencyChange: (freq: PrePaymentFrequencyOption) =>
                      this.store.selectPaymentFrequency(freq.pre_payment_frequency),
              }
            : undefined;

        return (
            <Card className="simple">
                <CardHeader>
                    <h5>Billing</h5>
                </CardHeader>
                <CardBody>
                    <BillingDetails subtotal={this.store.selectedOptionsCost} {...paymentFrequencyProps} />
                    {paymentFrequencyProps &&
                        paymentFrequencyProps.prePaymentFrequency.pre_payment_frequency !== 'post-paid' && (
                            <p>
                                After creating your Server order, we will invoice you for the first{' '}
                                {paymentFrequencyProps.prePaymentFrequency.pre_payment_period_months === 1
                                    ? 'month'
                                    : `${paymentFrequencyProps.prePaymentFrequency.pre_payment_period_months} months`}
                                . You will then be automatically billed every{' '}
                                {paymentFrequencyProps.prePaymentFrequency.pre_payment_period_months === 1
                                    ? 'month'
                                    : `${paymentFrequencyProps.prePaymentFrequency.pre_payment_period_months} months`}
                                .
                            </p>
                        )}

                    <p>
                        {getAppContext().config.brandName} has no minimum contract length and you may cancel your Server
                        any time.
                    </p>
                    {paymentFrequencyProps?.prePaymentFrequency.pre_payment_frequency !== 'post-paid' ? (
                        <p>
                            For payments made by credit card, your Server will be immediately created. This process will
                            take about two minutes, during which progress information shall be displayed to you. Once
                            your Server is created and is online, we will bill your credit card and provide order
                            confirmation details including your assigned IP address and the Server login Password.
                        </p>
                    ) : (
                        <p>
                            Your Server will be immediately created. This process will take about two minutes, during
                            which progress information shall be displayed to you. Once your Server is created and is
                            online, we will provide order confirmation details including your assigned IP address and
                            the Server login Password.
                        </p>
                    )}
                </CardBody>
            </Card>
        );
    }

    render() {
        const subtotal = this.store.selectedOptionsCost;

        if (this.store.unpaidInvoices.item.length > 0) {
            return (
                <div className="serverAddPage">
                    <h3 className="mb-4">New Server</h3>
                    <UnpaidInvoiceBlock unpaidCount={this.store.unpaidInvoices.item.length} />
                </div>
            );
        }

        return (
            <div className="serverAddPage">
                <ViewInMpanel section="add-server" />
                <h3 className="mb-4">New Server</h3>

                <ServerAddForm store={this.store} />

                {subtotal > 0 && this.renderBillingSummary()}
                <Card className="mb-4">
                    <CardBody>
                        {this.renderAgreeTerms()}
                        <AsyncButton size="lg" color="primary" onClick={() => this.doAddServer()}>
                            Add Server
                        </AsyncButton>
                    </CardBody>
                </Card>
            </div>
        );
    }
}
